import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const DEFAULT_CENTER = [-14.235, -51.9253];
const DEFAULT_ZOOM = 5;
const POINT_ZOOM = 16;

/**
 * Mapa Leaflet sem react-leaflet (compatível com CRA 4 / webpack sem transpilar node_modules).
 *
 * @param {Object} props
 * @param {[number, number]} props.center - [lat, lng]
 * @param {{ lat: number, lng: number }|null} props.markerPosition
 * @param {function(number, number)} props.onPositionChange
 * @param {function(number, number)|undefined} props.onMapClick
 * @param {boolean} [props.disabled]
 * @param {number} [props.height]
 * @param {number} [props.recenterKey] — ao mudar (ex.: incremento), o mapa centraliza no pino ou em `center` sem recriar o marcador
 */
export default function ClientAddressMap({
  center,
  markerPosition,
  onPositionChange,
  onMapClick,
  disabled = false,
  height = 240,
  recenterKey = 0,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const handlersRef = useRef({
    onPositionChange,
    onMapClick,
    disabled,
  });

  useEffect(() => {
    handlersRef.current = {
      onPositionChange,
      onMapClick,
      disabled,
    };
  }, [onPositionChange, onMapClick, disabled]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || mapRef.current) return;

    const map = L.map(el, {
      scrollWheelZoom: true,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);

    const onClick = (e) => {
      const { onMapClick: clickFn, disabled: off } = handlersRef.current;
      if (off || !clickFn) return;
      clickFn(e.latlng.lat, e.latlng.lng);
    };
    map.on("click", onClick);

    mapRef.current = map;

    return () => {
      map.off("click", onClick);
      if (markerRef.current) {
        map.removeLayer(markerRef.current);
        markerRef.current = null;
      }
      map.remove();
      mapRef.current = null;
    };
  }, []);

  const lat = center && center.length === 2 ? center[0] : DEFAULT_CENTER[0];
  const lng = center && center.length === 2 ? center[1] : DEFAULT_CENTER[1];
  const zoom = markerPosition ? POINT_ZOOM : DEFAULT_ZOOM;

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    map.setView([lat, lng], zoom);

    if (markerRef.current) {
      map.removeLayer(markerRef.current);
      markerRef.current = null;
    }

    if (markerPosition) {
      const m = L.marker([markerPosition.lat, markerPosition.lng], {
        draggable: !disabled,
      });
      m.on("dragend", (e) => {
        const p = e.target.getLatLng();
        handlersRef.current.onPositionChange(p.lat, p.lng);
      });
      m.addTo(map);
      markerRef.current = m;
    }

    if (disabled) {
      map.dragging.disable();
      map.touchZoom.disable();
      map.doubleClickZoom.disable();
    } else {
      map.dragging.enable();
      map.touchZoom.enable();
      map.doubleClickZoom.enable();
    }
  }, [
    lat,
    lng,
    zoom,
    markerPosition && markerPosition.lat,
    markerPosition && markerPosition.lng,
    disabled,
  ]);

  useEffect(() => {
    if (!recenterKey) return;
    const map = mapRef.current;
    if (!map) return;
    const z = markerPosition ? POINT_ZOOM : DEFAULT_ZOOM;
    const targetLat = markerPosition ? markerPosition.lat : lat;
    const targetLng = markerPosition ? markerPosition.lng : lng;
    map.setView([targetLat, targetLng], z);
    // Somente quando o usuário pede recentralizar (recenterKey), não ao mover o pino.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recenterKey]);

  return (
    <div
      ref={containerRef}
      style={{ height, width: "100%", borderRadius: 4, overflow: "hidden" }}
    />
  );
}
